import React, { useCallback, useEffect, useRef, useState } from "react";
import {
	APIProvider,
	Map,
	useMapsLibrary,
	AdvancedMarker,
	InfoWindow,
	useAdvancedMarkerRef,
	useMap,
	Pin,
} from '@vis.gl/react-google-maps';
import { useRecoilState } from 'recoil';
import {
	baseIdState,
	baseLatState,
	baseLngState,
	calendarViewState,
	currentDate,
	dateOfServiceState,
	dateWeekServiceState,
	dayHoverState,
	dayOfServiceState,
	googleApiKeyState,
	keyMapState,
	markersListState,
	resourceListState,
	waypointsState,
	weeklyItineraryState,
} from "../recoil/atoms";

const days = [
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
	'friday',
	'saturday',
	'sunday'
];

const week = {
	'sunday': '#343A40',
	'monday': '#E63946',
	'tuesday': '#53A551',
	'wednesday': 'rgba(255,0,213,0.53)',
	'thursday': '#457B9D',
	'friday': '#d65745',
	'saturday': '#9B5DE5',
}

export const colorList = [
	'#343A40',
	'#E63946',
	'#53A551',
	'rgba(255,0,213,0.53)',
	'#457B9D',
	'#d65745',
	'#9B5DE5',
	'#272c31',
	'#d7313f',
	'#499446',
	'rgba(238,4,198,0.53)',
	'#1d4159',
	'#ab3d2d',
	'#873be1',
	'#272a2d',
	'#fd3140',
	'#59ee56',
	'rgba(241,32,205,0.53)',
	'#3584b4',
	'#a93020',
]

const CANONICAL_PLACE_ID = /^ChIJ[\w-]{5,}$/;
const hasValidCoordinates = ( lat, lng ) => {
	return lat !== undefined && lng !== undefined &&
		lat !== '' && lng !== '' &&
		!Number.isNaN( +lat ) && !Number.isNaN( +lng );
};

const isCanonicalPlaceId = ( value = '' ) => CANONICAL_PLACE_ID.test( value );

const extractEmbeddedPlaceId = ( value = '' ) => {
	const matches = value.match( /(ChIJ[\w-]+)/g );
	return matches ? matches.pop() : null;
};

const decodeUtf8 = ( bytes ) => {
	if ( typeof TextDecoder !== 'undefined' ) {
		return new TextDecoder( 'utf-8' ).decode( bytes );
	}

	let result = '';
	bytes.forEach( ( byte ) => {
		result += String.fromCharCode( byte );
	} );

	try {
		return decodeURIComponent( escape( result ) );
	} catch ( error ) {
		return result;
	}
};

const readVarint = ( bytes, startIndex ) => {
	let result = 0;
	let shift = 0;
	let index = startIndex;

	while ( index < bytes.length ) {
		const byte = bytes[ index ];
		result |= ( byte & 0x7F ) << shift;
		index += 1;

		if ( ( byte & 0x80 ) === 0 ) {
			return { value: result, nextIndex: index };
		}

		shift += 7;
	}

	return { value: null, nextIndex: index };
};

const base64Decode = ( value ) => {
	if ( !value ) return null;

	const sanitized = value.replace( /\s+/g, '' ).replace( /-/g, '+' ).replace( /_/g, '/' );
	const pad = ( 4 - ( sanitized.length % 4 ) ) % 4;
	const padded = sanitized + '='.repeat( pad );

	try {
		if ( typeof atob === 'function' ) {
			return atob( padded );
		}

		if ( typeof Buffer !== 'undefined' ) {
			return Buffer.from( padded, 'base64' ).toString( 'binary' );
		}
	} catch ( error ) {
		console.warn( 'Failed to decode place_id payload', error );
	}

	return null;
};

const decodeProtoAddress = ( value ) => {
	try {
		const decoded = base64Decode( value );
		if ( !decoded ) return null;

		const bytes = Uint8Array.from( decoded, ( char ) => char.charCodeAt( 0 ) );
		if ( bytes.length === 0 || bytes[ 0 ] !== 0x12 ) return null;

		const { value: length, nextIndex } = readVarint( bytes, 1 );
		if ( !length || bytes.length < nextIndex + length ) return null;

		const addressBytes = bytes.slice( nextIndex, nextIndex + length );
		const address = decodeUtf8( addressBytes ).trim();

		return address && address.length ? address : null;
	} catch ( error ) {
		console.warn( 'Failed to read encoded place_id payload', error );
		return null;
	}
};

const normalizePlaceId = async ( placeId, geocodingLibrary ) => {
	if ( !placeId ) return '';

	const trimmed = placeId.trim();
	if ( isCanonicalPlaceId( trimmed ) ) return trimmed;

	const embedded = extractEmbeddedPlaceId( trimmed );
	if ( embedded ) return embedded;

	if ( !trimmed.startsWith( 'Ei' ) ) {
		return trimmed;
	}

	const address = decodeProtoAddress( trimmed );
	if ( !address || !geocodingLibrary ) return trimmed;

	try {
		const geocoder = new geocodingLibrary.Geocoder();
		const response = await geocoder.geocode( { address } );
		if ( response.results && response.results[ 0 ] ) {
			return response.results[ 0 ].place_id || trimmed;
		}
	} catch ( error ) {
		console.warn( `Unable to normalize place_id ${trimmed}`, error );
	}

	return trimmed;
};

const normalizePlaceIds = async ( placeIds = [], geocodingLibrary ) => {
	if ( !placeIds.length ) return [];
	if ( !geocodingLibrary ) return [ ...placeIds ];

	const normalized = [];
	for ( const id of placeIds ) {
		// eslint-disable-next-line no-await-in-loop
		normalized.push( await normalizePlaceId( id, geocodingLibrary ) );
	}

	return normalized;
};

const SingleMarker = (
	{
		address,
		position,
		orderId,
		jobName,
		jobUrl,
		day,
		service,
		salesForce,
		dateOfService,
		resourceId,
	} ) => {
	const [ markerRef, marker ] = useAdvancedMarkerRef();
	const [ infoWindowData, setInfoWindowData ] = useState( null );
	const [ calendarView, setCalendarView ] = useRecoilState( calendarViewState )
	const [ resources, setResources ] = useRecoilState( resourceListState )

	if ( !position || isNaN( position.lat ) || isNaN( position.lng ) ) {
		console.warn( `Invalid position for address: ${address}` );
		return null;
	}
	let titleWindow = `#${orderId} ${jobName}`;
	let colorMarker = '#fb043e';

	if ( calendarView === 'resourceTimeGridDay' ) {
		let idx = undefined;
		resources && resources.forEach( ( el, i ) => {
			if ( resourceId === +el.id ) {
				idx = i;
			}
		} )
		colorMarker = colorList[ idx ] ? colorList[ idx ] : '#fb043e'
	} else if ( calendarView === 'timeGridWeek' || calendarView === 'timeGridDay') {
		colorMarker = week[ day ] ? week[ day ] : '#fb043e';
	}

	return (
		<>
			<AdvancedMarker
				ref={markerRef}
				position={position}
				onClick={() => {
					setInfoWindowData( {
						position: position,
						content: address
					} );
				}}
				title={titleWindow}>
				<Pin
					background={colorMarker}
					glyphColor={'#fff'}
					borderColor={'#000'}
				/>
			</AdvancedMarker>
			{infoWindowData && (
				<InfoWindow
					pixelOffset={[ 0, -30 ]}
					headerContent={titleWindow}
					position={infoWindowData.position}
					onCloseClick={() => setInfoWindowData( null )}>
					<InfoWindowContent
						address={infoWindowData.content}
						orderId={orderId}
						jobName={jobName}
						jobUrl={jobUrl}
						service={service}
						salesForce={salesForce}
						dateOfService={dateOfService}
					/>
				</InfoWindow>
			)}
		</>
	);
}

const InfoWindowContent = ( {
								address,
								orderId,
								jobName,
								jobUrl,
								salesForce,
								dateOfService,
								service,
							} ) => {
	return (
		<div className="info-window-content">
			<div className="d-flex my-3">
				<div>{moment( dateOfService ).format( 'hh:mm A' )}</div>
				{salesForce && <div className="mx-1"> - {salesForce}</div>}
				<div className="service-type">{service}</div>
			</div>
			<div>
				<b>{address}</b>
			</div>
			<div>
				<a href={jobUrl} className="btn btn-primary my-3" target="_blank" tabIndex={-1}>Go to Order</a>
			</div>
		</div>
	);
}

const Markers = () => {
	const [ itinerary, setItinerary ] = useRecoilState( weeklyItineraryState );
	const [ markers, setMarkers ] = useRecoilState( markersListState );
	const geocodingLibrary = useMapsLibrary( 'geocoding' );
	const [ currentDate, setCurrentDay ] = useRecoilState( dateOfServiceState );
	const [ resources, setResources ] = useRecoilState( resourceListState );

	const [ calendarView, setCalendarView ] = useRecoilState( calendarViewState );

	const getLocationCoordinates = async ( location ) => {
		if ( location.lat !== null && location.lng !== null ) {
			return {
				lat: +location.lat,
				lng: +location.lng
			}
		} else {
			const geocoder = new geocodingLibrary.Geocoder();
			const normalizedPlaceId = await normalizePlaceId( location.place_id, geocodingLibrary );
			const response = await geocoder.geocode( {
				placeId: normalizedPlaceId
			} );
			if ( !response.results || !response.results[ 0 ] ) {
				throw new Error( `Unable to geocode place_id ${location.place_id}` );
			}
			return {
				lat: response.results[ 0 ].geometry.location.lat(),
				lng: response.results[ 0 ].geometry.location.lng()
			}
		}
	}
	useEffect( () => {
		if ( !geocodingLibrary || !itinerary ) return;

		if ( calendarView === 'timeGridWeek' || calendarView === 'timeGridDay' || calendarView === 'resourceTimeGridDay' ) {
			const fetchCoordinates = async () => {
				const newMarkers = [];

				for ( const day of itinerary ) {
					for ( const location of day.locations ) {
						try {
							let positions = await getLocationCoordinates( location );
							if ( positions && currentDate === day.date && (calendarView === 'timeGridDay' || calendarView === 'resourceTimeGridDay') ) {
								newMarkers.push( {
									address: location.address,
									position: positions,
									job_name: location.job_name,
									order_id: location.id,
									day: day.day_of_week,
									resourceId: day.resourceId,
									service_icon: location.service_icon,
									show_url: location.show_url,
									sales_force_acc_name: location.sales_force_acc_name,
									date_of_service: location.date_of_service,
								} );
							} else {
								newMarkers.push( {
									address: location.address,
									position: positions,
									job_name: location.job_name,
									order_id: location.id,
									day: day.day_of_week,
									resourceId: day.resourceId,
									service_icon: location.service_icon,
									show_url: location.show_url,
									sales_force_acc_name: location.sales_force_acc_name,
									date_of_service: location.date_of_service,
								} );
							}
						} catch ( error ) {
							console.warn( `Error geocoding address: ${location.address}`, error );
						}
					}
				}

				// Remove duplicate markers with the same address
				const uniqueMarkers = newMarkers.filter( ( marker, index, self ) =>
					index === self.findIndex( ( m ) => m.address === marker.address )
				);
				setMarkers( uniqueMarkers );
			};

			fetchCoordinates();
		}
	}, [ geocodingLibrary, itinerary, calendarView ] );

	if ( !itinerary.length || !markers.length ) return null;

	return (
		<>
			{markers.map( ( marker, index ) => {
				return (

					<SingleMarker
						key={`${marker.address}-${index}`}
						address={marker.address}
						position={marker.position}
						orderId={marker.order_id}
						jobName={marker.job_name}
						jobUrl={marker.show_url}
						resourceId={marker.resourceId}
						service={marker.service_icon}
						day={marker.day}
						salesForce={marker.sales_force_acc_name}
						dateOfService={marker.date_of_service}
					/>
				)
			} )}
		</>
	);
}

const Gmaps = ( callback, deps ) => {
	const [ googleApiKey, setGoogleApiKey ] = useRecoilState( googleApiKeyState );
	const [ baseLng, setBaseLng ] = useRecoilState( baseLngState );
	const [ baseLat, setBaseLat ] = useRecoilState( baseLatState );
	const [ basePlaceId ] = useRecoilState( baseIdState );
	const [ calendarView, setCalendarView ] = useRecoilState( calendarViewState );
	const [ key, setKey ] = useRecoilState( keyMapState );
	const [ markers, setMarkers ] = useRecoilState( markersListState );
	const defaultZoom = 12;
	const [zoomValue, setZoomValue] = useState(defaultZoom);

	const [ center, setCenter ] = useState({
		lat: +baseLat,
		lng: +baseLng,
	});

	const clearMap = () => {
		setKey( key + 1 );
		setMarkers( [] );
	}

	const handleCameraChange = useCallback( ( ev ) => {
			setZoomValue( ev.detail.zoom !== defaultZoom ? ev.detail.zoom : defaultZoom );
			// setCenter( (ev.detail.center.lat !== center.lat && ev.detail.center.lng !== center.lng) ? {
			// 	lat: +ev.detail.center.lat,
			// 	lng: +ev.detail.center.lat,
			// } : {
			// 	lat: +baseLat,
			// 	lng: +baseLng,
			// } );
		}
	);

	useEffect( () => {
		if ( hasValidCoordinates( baseLat, baseLng ) ) {
			setCenter( {
				lat: +baseLat,
				lng: +baseLng,
			} );
		}
	}, [ baseLat, baseLng ] );

	if ( googleApiKey === undefined || baseLng === undefined || baseLat === undefined ) {
		return <div className="w-100 h-100 d-flex align-items-center justify-content-center ">
			<div className="spinner-border">
				<span className="sr-only">Loading...</span>
			</div>
		</div>;
	}

	return <APIProvider
		apiKey={googleApiKey}
		language='en'
	>
		<BaseLocationInitializer
			basePlaceId={basePlaceId}
			baseLat={baseLat}
			baseLng={baseLng}
			setBaseLat={setBaseLat}
			setBaseLng={setBaseLng}
			updateCenter={setCenter}
		/>
		<Map
			id={key}
			defaultCenter={center}
			// center={center}
			mapId={'ROUTE-MAP'}
			// defaultZoom={zoomValue}
			zoom={zoomValue}
			onCameraChanged={handleCameraChange}
			gestureHandling={'greedy'}
			fullscreenControl={false}>
			<Directions clearMap={() => clearMap()} />
			<Markers />
		</Map>
		{calendarView === 'timeGridWeek' && <LegendWeek />}
	</APIProvider>
}

function BaseLocationInitializer ( {
									  basePlaceId,
									  baseLat,
									  baseLng,
									  setBaseLat,
									  setBaseLng,
									  updateCenter
								  } ) {
	const geocodingLibrary = useMapsLibrary( 'geocoding' );
	const lastResolvedPlaceId = useRef( null );

	useEffect( () => {
		if ( !geocodingLibrary || !basePlaceId ) return;

		const alreadyResolved = hasValidCoordinates( baseLat, baseLng ) &&
			lastResolvedPlaceId.current === basePlaceId;

		if ( alreadyResolved ) return;

		let cancelled = false;
		const resolveBaseCoordinates = async () => {
			try {
				const normalizedPlaceId = await normalizePlaceId( basePlaceId, geocodingLibrary );
				if ( !normalizedPlaceId || cancelled ) return;

				const geocoder = new geocodingLibrary.Geocoder();
				const response = await geocoder.geocode( { placeId: normalizedPlaceId } );
				const result = response.results && response.results[ 0 ];
				if ( !result || cancelled ) return;

				const lat = result.geometry.location.lat();
				const lng = result.geometry.location.lng();
				setBaseLat( lat.toString() );
				setBaseLng( lng.toString() );
				updateCenter && updateCenter( { lat, lng } );
				lastResolvedPlaceId.current = basePlaceId;
			} catch ( error ) {
				console.warn( 'Failed to resolve base location coordinates', error );
			}
		};

		resolveBaseCoordinates();

		return () => {
			cancelled = true;
		};
	}, [ geocodingLibrary, basePlaceId, baseLat, baseLng, setBaseLat, setBaseLng, updateCenter ] );

	return null;
}

function LegendWeek () {
	const [ dayHover, setDayHover ] = useRecoilState( dayHoverState );

	const styleW = {
		"position": "absolute",
		"top": "12px",
		"left": "auto",
		"right": "30px",
		"backgroundColor": "white",
		"width": "90px",
		"textTransform": "capitalize",
	};

	const DayLegend = ( { day, color } ) => {

		return <div
			style={{
				"display": "flex",
				"alignItems": "center",
				"padding": "1px 8px",
				"justifyContent": "space-between",
				"borderBottom": "1px solid #ddd",
				"cursor": "pointer"
			}}
			onMouseEnter={() => {setDayHover( day )}}
			onMouseLeave={() => {setDayHover( undefined )}}
		>{day.slice( 0, 3 )}: <div style={{
			"width": "20px",
			"height": "5px",
			"backgroundColor": color,
		}}></div> </div>
	}

	return <div
		style={styleW}
	>{Object.entries( week ).map( ( [ day, color ] ) => {
		return <DayLegend
			key={day}
			day={day}
			color={color}
		/>
	} )}</div>;
}

function Directions ( { clearMap } ) {
	const map = useMap();
	const routesLibrary = useMapsLibrary( 'routes' );
	const geocodingLibrary = useMapsLibrary( 'geocoding' );
	const [ directionsService, setDirectionsService ] = useState();
	const [ directionsRenderer, setDirectionsRenderer ] = useState();
	const [ dayOfService, setDayOfService ] = useRecoilState( dayOfServiceState );
	const [ waypoints, setWaypoints ] = useRecoilState( waypointsState );
	const [ calendarView, setCalendarView ] = useRecoilState( calendarViewState );
	const [ itinerary, setItinerary ] = useRecoilState( weeklyItineraryState );
	const [ currentWeekDate, setCurrentWeekDate ] = useRecoilState( dateWeekServiceState );
	const [ markers, setMarkers ] = useRecoilState( markersListState );
	const [ basePlaceId, setBasePlaceId ] = useRecoilState( baseIdState );
	const [ baseLng, setBaseLng ] = useRecoilState( baseLngState );
	const [ baseLat, setBaseLat ] = useRecoilState( baseLatState );
	const [ currentDate, setCurrentDay ] = useRecoilState( dateOfServiceState );
	const [ resources, setResources ] = useRecoilState( resourceListState );
	const center = {
		lat: +baseLat,
		lng: +baseLng,
	}

	// Initialize directions service and renderer
	useEffect( () => {
		if ( !routesLibrary || !map ) return;
		setDirectionsService( new routesLibrary.DirectionsService() );
		setDirectionsRenderer( new routesLibrary.DirectionsRenderer( { map } ) );
	}, [ routesLibrary, map ] );

	useEffect( () => {
		if ( calendarView ) {
			clearMap();
		}
	}, [ currentWeekDate, currentDate, itinerary ] )

	const setMarkersList = async ( locs, day ) => {
		if ( !geocodingLibrary || !itinerary ) return;
		const geocoder = new geocodingLibrary.Geocoder();

		const newMarkers = [];
		for ( const location of locs ) {
			try {
				const response = await geocoder.geocode( {
					placeId: await normalizePlaceId( location.place_id, geocodingLibrary )
				} );

				if ( response.results && response.results[ 0 ] ) {
					newMarkers.push( {
						address: location.address,
						position: {
							lat: response.results[ 0 ].geometry.location.lat(),
							lng: response.results[ 0 ].geometry.location.lng()
						},
						job_name: location.job_name,
						order_id: location.id,
						service_icon: location.service_icon,
						show_url: location.show_url,
						day: day,
						sales_force_acc_name: location.sales_force_acc_name,
						date_of_service: location.date_of_service,
					} );
				}
			} catch ( error ) {
				console.warn( `Error geocoding address: ${location.address}`, error );
			}
		}
		if ( newMarkers !== markers ) {
			// setMarkers( newMarkers );
		}
		setMarkers( newMarkers );
	}

	useEffect( () => {
		if ( !map || !geocodingLibrary ) return;

		const renderRoutes = async () => {
			if ( dayOfService && calendarView === 'timeGridDay' ) {
				clearRoutes();
				if ( waypoints.length > 1 ) {
					let routeDetails = '';
					itinerary.forEach( i => {
						if ( dayOfService === i.day_of_week && i.locations.length > 0 ) {
							routeDetails = i;
						}
					} );
					const normalizedWaypoints = await normalizePlaceIds( waypoints, geocodingLibrary );
					init_Route( map, dayOfService, normalizedWaypoints, routeDetails );
				}
			} else if ( calendarView === 'timeGridWeek' ) {
				clearRoutes();

				for ( const el of days ) {
					for ( const i of itinerary ) {
						if ( el === i.day_of_week && i.locations.length > 0 ) {
							const _waypoints = [ basePlaceId ];
							i.locations.forEach( wp => _waypoints.push( wp.place_id ) );
							// eslint-disable-next-line no-await-in-loop
							const normalizedWaypoints = await normalizePlaceIds( _waypoints, geocodingLibrary );
							init_Route( map, i.day_of_week, normalizedWaypoints, i );
						}
					}
				}
			} else if ( dayOfService && calendarView === 'resourceTimeGridDay' ) {
				clearRoutes();

				for ( const i of itinerary ) {
					if ( currentDate === i.date && i.locations.length > 0 ) {
						const _waypoints = [ basePlaceId ];
						i.locations.forEach( wp => _waypoints.push( wp.place_id ) );
						// eslint-disable-next-line no-await-in-loop
						const normalizedWaypoints = await normalizePlaceIds( _waypoints, geocodingLibrary );
						let idxUser = undefined;
						resources && resources.forEach( ( el, index ) => {
							if ( i.resourceId === +el.id ) {

								idxUser = index;
							}
						} )
						init_Route( map, i.day_of_week, normalizedWaypoints, i, idxUser );
					}
				}
			}
		};

		renderRoutes();
	}, [ dayOfService, waypoints, calendarView, map, itinerary, geocodingLibrary, basePlaceId, currentDate, resources ] )

	const clearRoutes = () => {
		// Clear any existing directions renderer
		if ( window.currentDirectionsDisplay ) {
			window.currentDirectionsDisplay.setMap( null );
		}
	}

	function init_Route ( map, day, waypoints_place_ids, routeDetails, resId ) {
		let directionsService = new google.maps.DirectionsService();

		let strokeColor = ( calendarView === 'resourceTimeGridDay') ? colorList[ resId ] : week[ day ];
		let directionsDisplay = new google.maps.DirectionsRenderer( {
			map: map,
			polylineOptions: {
				strokeWeight: 5,
				strokeColor: strokeColor,
				strokeOpacity: 0.7
			},
			suppressMarkers: true  // Hide default markers
		} );

		// Store the current renderer globally so we can clear it later
		window.currentDirectionsDisplay = directionsDisplay;

		let destination;
		let _waypoints = [];
		if ( waypoints_place_ids ) {
			let _wp = [ ...waypoints_place_ids ];
			_wp.slice( 1, -1 ).forEach( function ( item ) {
				_waypoints.push(
					{
						stopover: true,
						location: { placeId: item }
					} );
			} )
			destination = _wp.pop();
			directionsService.route( {
				origin: { placeId: waypoints_place_ids[ 0 ] },
				destination: { placeId: destination },
				waypoints: _waypoints,
				travelMode: google.maps.TravelMode.DRIVING
			}, function ( response, status ) {
				if ( status === 'OK' ) {
					directionsDisplay.setDirections( response );

					const route = response.routes[ 0 ].legs;
					const startLeg = route && route[ 0 ];
					const basePosition = startLeg
						? {
							lat: startLeg.start_location.lat(),
							lng: startLeg.start_location.lng()
						}
						: center;

					// Start point marker
					new google.maps.Marker( {
						position: basePosition,
						map: map,
						label: {
							text: 'Base',
							color: 'white'
						},
						icon: {
							path: google.maps.SymbolPath.CIRCLE,
							scale: 19,
							fillColor: strokeColor,
							fillOpacity: 1,
							strokeColor: 'white',
							strokeWeight: 2,
						},
						clickable: true,

					} );
				} else {
					console.log( status, destination )
				}
			} );
		}
	}

	return null;
}

export default Gmaps;
